// Message Composer Component
// Text input area for composing and sending messages

import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Menu,
  MenuItem,
  Popover,
  Divider,
  Tooltip,
  useTheme,
  Button
} from '@mui/material';
import {
  Send,
  AttachFile,
  EmojiEmotions,
  Close,
  Share,
  PriorityHigh,
  PhotoCamera,
  Videocam,
  LabelOutlined,
  ImageOutlined,
  MedicationOutlined,
  NotesOutlined
} from '@mui/icons-material';
import { useAuthState } from 'react-firebase-hooks/auth';

// Services
import { sendMessage } from '../../services/messaging';
import { MessageTypes, MessagePriority } from '../../models/messaging';
import { getMessagingTheme } from '../../assets/theme/messagingTheme';
import { auth } from '../../services/firebase';
import { useChatAttachmentSend } from '../../hooks/messaging/useChatAttachmentSend';
import {
  LOG_TYPES,
  TAG_TYPES,
  getTagTypeById,
  getTimelineMetaForCategory,
} from '../../constants/logTypeRegistry';

const renderChipIcon = (IconComponent, icon) => {
  if (IconComponent) {
    return <IconComponent sx={{ fontSize: 16, color: 'inherit' }} />;
  }

  if (icon) {
    return <span aria-hidden="true">{icon}</span>;
  }

  return null;
};

/**
 * Message priority selector
 */
const PrioritySelector = ({ priority, onPriorityChange, anchorEl, onClose }) => {
  const priorities = [
    { value: MessagePriority.NORMAL, label: 'Normal', icon: '💬', color: 'default' },
    { value: MessagePriority.URGENT, label: 'Urgent', icon: '🚨', color: 'error' }
  ];

  return (
    <Menu
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={onClose}
      transformOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'top' }}
    >
      {priorities.map((p) => (
        <MenuItem
          key={p.value}
          selected={priority === p.value}
          onClick={() => {
            onPriorityChange(p.value);
            onClose();
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>{p.icon}</span>
            <Typography>{p.label}</Typography>
          </Box>
        </MenuItem>
      ))}
    </Menu>
  );
};

/**
 * Reply preview component
 */
const ReplyPreview = ({ replyToMessage, onCancelReply }) => {
  if (!replyToMessage) return null;

  return (
    <Box
      sx={{
        p: 1.5,
        backgroundColor: 'action.hover',
        borderLeft: 4,
        borderColor: 'primary.main',
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}
    >
      <Box sx={{ flex: 1 }}>
        <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600 }}>
          Replying to {replyToMessage.senderName}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            mt: 0.25
          }}
        >
          {replyToMessage.text || `${replyToMessage.type} message`}
        </Typography>
      </Box>
      <IconButton size="small" onClick={onCancelReply}>
        <Close />
      </IconButton>
    </Box>
  );
};

/**
 * Main MessageComposer component
 */
const MessageComposer = ({
  conversationId,
  childId = null,
  currentUserId,
  onMessageSent,
  isMobile = false,
  replyToMessage = null,
  onCancelReply = null,
  onQuickActionMedication = null,
  onQuickActionUpdate = null,
}) => {
  const theme = useTheme();
  const messagingTheme = getMessagingTheme(theme);
  const [user] = useAuthState(auth);
  
  // State management
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [priority, setPriority] = useState(MessagePriority.NORMAL);
  const [priorityAnchor, setPriorityAnchor] = useState(null);
  const [contextAnchor, setContextAnchor] = useState(null);
  const [photoActionAnchor, setPhotoActionAnchor] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedTags, setSelectedTags] = useState([]);

  // Refs
  const textFieldRef = useRef(null);
  const fileInputRef = useRef(null);
  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const attachmentSend = useChatAttachmentSend({
    context: {
      conversationId, childId, currentUserId,
      senderName: user?.displayName || user?.email || 'Unknown User',
      category: selectedCategory, tags: selectedTags, priority,
    },
    onMessageSent,
    onSuccess: () => {
      setSelectedCategory('');
      setSelectedTags([]);
      setContextAnchor(null);
    },
    setSending,
    setError,
  });

  /**
   * Handle message text change
   */
  const handleTextChange = (event) => {
    setMessageText(event.target.value);
    setError(null); // Clear any previous errors
  };

  /**
   * Handle Enter key press
   */
  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  /**
   * Send message
   */
  const handleSendMessage = async () => {
    const trimmedText = messageText.trim();
    
    // Validation
    if (!trimmedText || !conversationId || !currentUserId) {
      return;
    }

    if (sending) {
      return; // Prevent double-sending
    }

    try {
      setSending(true);
      setError(null);

      console.log('📤 Sending message:', { conversationId, text: trimmedText, priority });

      // Prepare message data
      const messageData = {
        conversationId,
        childId,
        senderId: currentUserId,
        senderName: user?.displayName || user?.email || 'Unknown User',
        text: trimmedText,
        type: MessageTypes.TEXT,
        category: selectedCategory || null,
        tags: [...selectedTags],
        priority,
        replyTo: replyToMessage?.id || null,
        metadata: {
          childId,
        }
      };

      const result = await sendMessage(messageData);

      if (result.success) {
        
        // Clear composer
        setMessageText('');
        attachmentSend.clearRetry();
        setPriority(MessagePriority.NORMAL);
        handleClearContext();
        
        // Cancel reply if active
        if (onCancelReply) {
          onCancelReply();
        }

        // Create message object for immediate UI update
        const sentMessage = {
          id: result.messageId,
          ...messageData,
          createdAt: new Date(),
          updatedAt: new Date(),
          readBy: { [currentUserId]: new Date() },
          isEdited: false,
          isDeleted: false
        };

        // Notify parent component
        onMessageSent?.(sentMessage);

        // Focus back to input
        textFieldRef.current?.focus();
      } else {
        console.error('Failed to send message:', result.error);
        setError(result.error || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setError(error.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  /**
   * Handle file attachment
   */
  const handleFileAttach = () => {
    fileInputRef.current?.click();
  };

  /**
   * Handle emoji picker (placeholder)
   */
  const handleEmojiPicker = () => {
    // TODO: Implement emoji picker
    console.log('😀 Emoji picker not yet implemented');
  };

  /**
   * Handle share content (for sharing incidents/timeline entries)
   */
  const handleShareContent = () => {
    // TODO: Implement content sharing
    console.log('🔗 Content sharing not yet implemented');
  };

  const handleContextClick = (event) => {
    setContextAnchor(event.currentTarget);
  };

  const handleContextClose = () => {
    setContextAnchor(null);
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory((current) => (current === category ? '' : category));
  };

  const handleTagToggle = (tagId) => {
    setSelectedTags((current) => (
      current.includes(tagId)
        ? current.filter((item) => item !== tagId)
        : [...current, tagId]
    ));
  };

  const handleClearContext = () => {
    setSelectedCategory('');
    setSelectedTags([]);
    setContextAnchor(null);
  };

  /**
   * Handle priority selection
   */
  const handlePriorityClick = (event) => {
    setPriorityAnchor(event.currentTarget);
  };

  const handlePriorityClose = () => {
    setPriorityAnchor(null);
  };

  const handlePriorityChange = (newPriority) => {
    setPriority(newPriority);
  };

  const handleCapturePhotoClick = () => {
    photoInputRef.current?.click();
  };

  const handleCaptureVideoClick = () => {
    videoInputRef.current?.click();
  };

  const handlePhotoQuickAction = (event) => {
    setPhotoActionAnchor(event.currentTarget);
  };

  const handlePhotoActionClose = () => {
    setPhotoActionAnchor(null);
  };

  const handleChoosePhotoLibrary = () => {
    handlePhotoActionClose();
    handleFileAttach();
  };

  const handleUseCamera = () => {
    handlePhotoActionClose();
    handleCapturePhotoClick();
  };

  /**
   * Upload a file to Firebase Storage and send a media message
   */
  const uploadAndSendMedia = async (file) => {
    await attachmentSend.sendFile(file);
  };

  // Focus input on mount
  useEffect(() => {
    if (!isMobile) {
      textFieldRef.current?.focus();
    }
  }, [conversationId, isMobile]);

  // Auto-resize text field (basic implementation)
  const maxRows = isMobile ? 3 : 4;
  const categoryOptions = [
    LOG_TYPES.sleep,
    LOG_TYPES.medication,
    LOG_TYPES.food,
    LOG_TYPES.bathroom,
    LOG_TYPES.behavior,
    LOG_TYPES.mood,
    LOG_TYPES.milestone,
    LOG_TYPES.health,
    LOG_TYPES.log,
  ];
  const tagOptions = Object.values(TAG_TYPES);
  const selectedCategoryMeta = selectedCategory ? getTimelineMetaForCategory(selectedCategory) : null;
  const selectedTagMetas = selectedTags
    .map((tagId) => {
      const tag = getTagTypeById(tagId);
      if (!tag) return null;
      return {
        ...tag,
        meta: getTimelineMetaForCategory(tag.category),
      };
    })
    .filter(Boolean);
  const isDesktop = !isMobile;
  const composerPlaceholder = isMobile ? 'Message care circle…' : 'Type your message...';

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: isDesktop ? 0 : '20px 20px 0 0',
        borderTop: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        boxShadow: isDesktop ? 'none' : '0 -4px 12px rgba(0,0,0,0.1)',
        backdropFilter: isDesktop ? 'none' : 'blur(10px)',
        background: isDesktop
          ? theme.palette.background.paper
          : `linear-gradient(to bottom, ${theme.palette.background.paper} 0%, ${theme.palette.grey[50]} 100%)`,
      }}
    >
      {/* Reply Preview */}
      <ReplyPreview replyToMessage={replyToMessage} onCancelReply={onCancelReply} />

      {/* Error Alert */}
      {error && (
        <Alert
          severity="error"
          sx={{ mx: 2, mt: 1, mb: 0 }}
          onClose={() => { setError(null); attachmentSend.clearRetry(); }}
          action={attachmentSend.hasRetry ? (
            <Button
              color="inherit"
              size="small"
              disabled={sending}
              onClick={attachmentSend.retry}
              aria-label={`Retry attachment${attachmentSend.retryFileName ? ` ${attachmentSend.retryFileName}` : ''}`}
            >
              Retry attachment
            </Button>
          ) : null}
        >
          {error}
        </Alert>
      )}

      {attachmentSend.activity && (
        <Box
          role="status"
          aria-live="polite"
          sx={{ mx: 2, mt: 1, display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}
        >
          <CircularProgress size={16} />
          <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
            {{
              uploading: `Uploading ${attachmentSend.activity.fileName}…`,
              sharing: `Sharing ${attachmentSend.activity.fileName} in chat…`,
              retrying: `Retrying ${attachmentSend.activity.fileName}…`,
              preparing: `Preparing ${attachmentSend.activity.fileName}…`,
            }[attachmentSend.activity.phase] || `Preparing ${attachmentSend.activity.fileName}…`}
          </Typography>
        </Box>
      )}

      {/* Priority Indicator */}
      {priority === MessagePriority.URGENT && (
        <Box sx={{ px: 2, pt: 1 }}>
          <Chip
            size="small"
            icon={<PriorityHigh />}
            label="Urgent Message"
            color="error"
            variant="outlined"
            onDelete={() => setPriority(MessagePriority.NORMAL)}
          />
        </Box>
      )}

      {/* Main Composer */}
      {isMobile ? (
        <>
          {/* Quick action row removed for now. */}

          <Box
            sx={{
              px: 2,
              pb: `calc(12px + env(safe-area-inset-bottom))`,
              pt: 0.5,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                p: 1,
                borderRadius: 999,
                border: `1px solid ${theme.palette.divider}`,
                bgcolor: '#FFFFFF',
                boxShadow: '0 10px 24px rgba(15, 23, 42, 0.08)',
              }}
            >
              <IconButton
                onClick={handleFileAttach}
                disabled={sending}
                aria-label="Attach image or document"
                sx={{ color: 'text.secondary' }}
              >
                <AttachFile />
              </IconButton>

              <TextField
                ref={textFieldRef}
                fullWidth
                multiline
                maxRows={3}
                placeholder={composerPlaceholder}
                value={messageText}
                onChange={handleTextChange}
                onKeyPress={handleKeyPress}
                disabled={sending}
                variant="standard"
                InputProps={{ disableUnderline: true }}
                sx={{
                  '& .MuiInputBase-root': {
                    fontSize: '1rem',
                    lineHeight: 1.45,
                  },
                  '& .MuiInputBase-input': {
                    py: 1,
                    '&::placeholder': {
                      color: theme.palette.text.secondary,
                      opacity: 0.72,
                    },
                  },
                }}
              />

              <IconButton
                onClick={handleSendMessage}
                disabled={!messageText.trim() || sending || !conversationId}
                aria-label="Send message"
                sx={{
                  width: 48,
                  height: 48,
                  bgcolor: theme.palette.primary.main,
                  color: '#fff',
                  '&:hover': {
                    bgcolor: theme.palette.primary.dark,
                  },
                  '&.Mui-disabled': {
                    bgcolor: theme.palette.action.disabledBackground,
                    color: theme.palette.action.disabled,
                  },
                }}
              >
                {sending ? <CircularProgress size={20} color="inherit" /> : <Send />}
              </IconButton>
            </Box>
          </Box>
        </>
      ) : (
      <Box
        sx={{
          display: 'flex',
          alignItems: isDesktop ? 'center' : 'flex-end',
          gap: 1,
          px: 3,
          py: isDesktop ? 2.25 : 2,
          flexWrap: isDesktop ? 'nowrap' : 'wrap',
          ...(priority === MessagePriority.URGENT && { pt: 1 })
        }}
      >
        {/* Action Buttons (Left) */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: isDesktop ? 'row' : 'column',
            alignItems: 'center',
            gap: 0.5,
            flexShrink: 0,
          }}
        >
          <Tooltip title="Attach file">
            <IconButton
              size="small"
              onClick={handleFileAttach}
              disabled={sending}
              aria-label="Attach image or document"
              sx={{ color: 'text.secondary' }}
            >
              <AttachFile />
            </IconButton>
          </Tooltip>

          {/* Quick camera capture on mobile */}
          {isMobile && (
            <>
              <Tooltip title="Take photo">
                <IconButton
                  size="small"
                  onClick={handleCapturePhotoClick}
                  disabled={sending}
                  sx={{ color: 'text.secondary' }}
                >
                  <PhotoCamera />
                </IconButton>
              </Tooltip>
              <Tooltip title="Record video">
                <IconButton
                  size="small"
                  onClick={handleCaptureVideoClick}
                  disabled={sending}
                  sx={{ color: 'text.secondary' }}
                >
                  <Videocam />
                </IconButton>
              </Tooltip>
            </>
          )}

          {!isMobile && (
            <Tooltip title="Share content">
              <IconButton
                size="small"
                onClick={handleShareContent}
                disabled={sending}
                sx={{ color: 'text.secondary' }}
              >
                <Share />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Text Input */}
        <TextField
          ref={textFieldRef}
          fullWidth
          multiline
          maxRows={maxRows}
          placeholder={composerPlaceholder}
          value={messageText}
          onChange={handleTextChange}
          onKeyPress={handleKeyPress}
          disabled={sending}
          variant="outlined"
          size="small"
          sx={{
            ...messagingTheme.search.input,
            flex: 1,
            '& .MuiOutlinedInput-root': {
              backgroundColor: theme.palette.background.default,
              minHeight: isDesktop ? 56 : 48,
              borderRadius: isDesktop ? '18px' : '24px',
              border: `1px solid ${theme.palette.divider}`,
              transition: messagingTheme.transitions.fast,
              alignItems: isDesktop ? 'center' : 'flex-start',
              '& fieldset': {
                border: 'none',
              },
              '&:hover': {
                borderColor: theme.palette.primary.light,
                boxShadow: '0 4px 12px rgba(0,0,0,0.06)',
              },
              '&.Mui-focused': {
                borderColor: theme.palette.primary.main,
                boxShadow: `0 0 0 3px ${theme.palette.primary.main}16`,
              },
            },
            '& .MuiInputBase-input': {
              py: isDesktop ? 1.35 : 1.5,
              px: 2,
              lineHeight: 1.4,
              fontSize: isDesktop ? '1rem' : '0.95rem',
              '&::placeholder': {
                color: theme.palette.text.secondary,
                opacity: 0.7,
              }
            }
          }}
        />

        {/* Action Buttons (Right) */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: isDesktop ? 'row' : 'column',
            alignItems: 'center',
            gap: 0.5,
            flexShrink: 0,
          }}
        >
          {!isMobile && (
            <Tooltip title="Add emoji">
              <IconButton
                size="small"
                onClick={handleEmojiPicker}
                disabled={sending}
                sx={{ color: 'text.secondary' }}
              >
                <EmojiEmotions />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title="Message priority">
            <IconButton
              size="small"
              onClick={handlePriorityClick}
              disabled={sending}
              sx={{
                color: priority === MessagePriority.URGENT ? 'error.main' : 'text.secondary'
              }}
            >
              <PriorityHigh />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Send Button */}
        <Tooltip title={messageText.trim() ? 'Send message (Enter)' : 'Enter a message to send'}>
          <Box>
            <IconButton
              onClick={handleSendMessage}
              disabled={!messageText.trim() || sending || !conversationId}
              sx={{
                ...messagingTheme.buttons.messages,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                width: isDesktop ? 52 : 48,
                height: isDesktop ? 52 : 48,
                borderRadius: '50%',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                transition: messagingTheme.transitions.fast,
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                  transform: 'scale(1.05) translateY(-1px)',
                  boxShadow: '0 6px 20px rgba(0,0,0,0.25)',
                },
                '&.Mui-disabled': {
                  backgroundColor: theme.palette.action.disabledBackground,
                  color: theme.palette.action.disabled,
                  transform: 'none',
                  boxShadow: 'none',
                }
              }}
            >
              {sending ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <Send />
              )}
            </IconButton>
          </Box>
        </Tooltip>
      </Box>
      )}

      {/* Context Summary */}
      {!isMobile ? (
      <Box
        sx={{
          px: 3,
          pb: selectedCategory || selectedTags.length ? 1 : 1.5,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 0.75,
          alignItems: 'center',
        }}
      >
        {selectedCategoryMeta ? (
          <Chip
            size="small"
            label={selectedCategoryMeta.label}
            icon={renderChipIcon(selectedCategoryMeta.iconComponent, selectedCategoryMeta.icon)}
            onDelete={() => setSelectedCategory('')}
            sx={{
              bgcolor: `${selectedCategoryMeta.color}18`,
              color: selectedCategoryMeta.color,
              borderColor: `${selectedCategoryMeta.color}44`,
              border: '1px solid',
              fontWeight: 700,
              '& .MuiChip-icon': { color: 'inherit' },
            }}
          />
        ) : null}

        {selectedTagMetas.map((tag) => (
          <Chip
            key={tag.id}
            size="small"
            label={tag.label}
            icon={renderChipIcon(tag.iconComponent || tag.meta?.iconComponent, tag.icon)}
            onDelete={() => handleTagToggle(tag.id)}
            sx={{
              bgcolor: `${tag.meta.color}18`,
              color: tag.meta.color,
              borderColor: `${tag.meta.color}44`,
              border: '1px solid',
              fontWeight: 700,
              '& .MuiChip-icon': { color: 'inherit' },
            }}
          />
        ))}

        <Chip
          size="small"
          clickable
          onClick={handleContextClick}
          icon={<LabelOutlined />}
          label={selectedCategory || selectedTags.length ? 'Add more context' : 'Add context'}
          variant={selectedCategory || selectedTags.length ? 'outlined' : 'filled'}
          sx={{
            fontWeight: 700,
            borderColor: 'rgba(37, 99, 235, 0.22)',
            color: 'primary.main',
            bgcolor: selectedCategory || selectedTags.length ? 'transparent' : 'rgba(239, 246, 255, 0.95)',
          }}
        />
      </Box>
      ) : null}

      <Popover
        open={Boolean(contextAnchor)}
        anchorEl={contextAnchor}
        onClose={handleContextClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              width: { xs: 320, sm: 420 },
              p: 2,
              borderRadius: 3,
              mt: 1,
            },
          },
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          Entry type
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {categoryOptions.map((type) => {
            const meta = getTimelineMetaForCategory(type.category);
            const isSelected = selectedCategory === type.category;
            return (
              <Chip
                key={type.id}
                label={type.filterLabel}
                icon={renderChipIcon(meta.iconComponent, type.icon)}
                onClick={() => handleCategorySelect(type.category)}
                variant={isSelected ? 'filled' : 'outlined'}
                color={isSelected ? 'primary' : 'default'}
                sx={{
                  borderColor: isSelected ? `${meta.color}55` : 'rgba(148, 163, 184, 0.22)',
                  bgcolor: isSelected ? `${meta.color}18` : 'background.paper',
                  color: isSelected ? meta.color : 'text.primary',
                  fontWeight: 700,
                  '& .MuiChip-icon': { color: 'inherit' },
                }}
              />
            );
          })}
        </Box>

        <Divider sx={{ my: 1.5 }} />

        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          Context tags
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {tagOptions.map((tag) => {
            const tagMeta = getTimelineMetaForCategory(tag.category);
            const isSelected = selectedTags.includes(tag.id);
            return (
              <Chip
                key={tag.id}
                label={tag.label}
                icon={renderChipIcon(tag.iconComponent || tagMeta.iconComponent, tag.icon)}
                onClick={() => handleTagToggle(tag.id)}
                variant={isSelected ? 'filled' : 'outlined'}
                color={isSelected ? 'primary' : 'default'}
                sx={{
                  borderColor: isSelected ? `${tagMeta.color}55` : 'rgba(148, 163, 184, 0.22)',
                  bgcolor: isSelected ? `${tagMeta.color}18` : 'background.paper',
                  color: isSelected ? tagMeta.color : 'text.primary',
                  fontWeight: 700,
                  '& .MuiChip-icon': { color: 'inherit' },
                }}
              />
            );
          })}
        </Box>
      </Popover>

      {/* Priority Selector Menu */}
      <PrioritySelector
        priority={priority}
        onPriorityChange={handlePriorityChange}
        anchorEl={priorityAnchor}
        onClose={handlePriorityClose}
      />

      <Menu
        anchorEl={photoActionAnchor}
        open={Boolean(photoActionAnchor)}
        onClose={handlePhotoActionClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <MenuItem onClick={handleChoosePhotoLibrary}>
          <ImageOutlined sx={{ mr: 1 }} />
          Choose from library
        </MenuItem>
        <MenuItem onClick={handleUseCamera}>
          <PhotoCamera sx={{ mr: 1 }} />
          Take photo
        </MenuItem>
      </Menu>

      {/* Hidden File Input (documents/library) */}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,video/*,.pdf,.doc,.docx"
        onChange={async (e) => {
          const files = Array.from(e.target.files || []);
          for (const f of files) {
            await uploadAndSendMedia(f);
          }
          // Allow re-selecting the same file(s)
          e.target.value = '';
        }}
        style={{ display: 'none' }}
      />

      {/* Hidden Photo Capture Input */}
      <input
        ref={photoInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          if (file) {
            await uploadAndSendMedia(file);
          }
          e.target.value = '';
        }}
        style={{ display: 'none' }}
      />

      {/* Hidden Video Capture Input */}
      <input
        ref={videoInputRef}
        type="file"
        accept="video/*"
        capture="environment"
        onChange={async (e) => {
          const file = e.target.files?.[0];
          if (file) {
            await uploadAndSendMedia(file);
          }
          e.target.value = '';
        }}
        style={{ display: 'none' }}
      />
    </Paper>
  );
};

export default MessageComposer;
